"use client"

import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import type { PublicStorefrontSettings } from "@modules/global-shell/types"
import styles from "./announcement-bar.module.css"

const AnnouncementBar = ({ settings }: { settings: PublicStorefrontSettings["announcement"] }) => {
  const marquee = settings.mode === "marquee"
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [duplicateCount, setDuplicateCount] = useState(marquee ? 2 : 1)

  useEffect(() => {
    if (!marquee) {
      setDuplicateCount(1)
      return
    }
    setDuplicateCount((value) => Math.max(2, value))
  }, [marquee])

  useEffect(() => {
    if (!marquee) return
    const update = () => {
      const wrapperWidth = wrapperRef.current?.offsetWidth ?? 0
      const renderedWidth = listRef.current?.scrollWidth ?? 0
      if (!wrapperWidth || !renderedWidth) return
      const baseWidth = renderedWidth / Math.max(1, duplicateCount)
      if (!baseWidth) return
      setDuplicateCount((current) => Math.max(current, 2, Math.ceil((wrapperWidth * 2) / baseWidth)))
    }
    update()
    const observer = new ResizeObserver(update)
    if (wrapperRef.current) observer.observe(wrapperRef.current)
    if (listRef.current) observer.observe(listRef.current)
    return () => observer.disconnect()
  }, [duplicateCount, marquee])

  const repeated = useMemo(() => {
    const count = marquee ? duplicateCount : 1
    return Array.from({ length: count }).flatMap((_, repeat) =>
      settings.items.map((text, index) => ({ text, repeat, index }))
    )
  }, [duplicateCount, marquee, settings.items])

  if (!settings.enabled || settings.items.length === 0) return null

  const rootClass = marquee
    ? `${styles["announcement-bar"]} ${styles["announcement-bar--marquee"]}`
    : styles["announcement-bar"]
  const listClass = marquee
    ? `${styles["announcement-bar__list"]} ${styles["announcement-bar__list--marquee"]}`
    : styles["announcement-bar__list"]
  const itemClass = marquee
    ? `${styles["announcement-bar__item"]} ${styles["announcement-bar__item--marquee"]}`
    : styles["announcement-bar__item"]
  const style = marquee
    ? ({
        "--announcement-bar-marquee-distance": `${100 / Math.max(1, duplicateCount)}%`,
        "--announcement-bar-marquee-duration": "30s",
      } as CSSProperties)
    : undefined

  return (
    <div className={rootClass} data-testid="global-announcement">
      <div className={styles["announcement-bar__inner"]} ref={wrapperRef}>
        <ul className={listClass} ref={listRef} style={style}>
          {repeated.map((item, position) => {
            const duplicate = marquee && item.repeat > 0
            const key = `${item.repeat}-${item.index}-${item.text}`
            return (
              <Fragment key={key}>
                <li aria-hidden={duplicate || undefined} className={itemClass}>
                  <p className={styles["announcement-bar__text"]}>{item.text}</p>
                </li>
                {position < repeated.length - 1 && marquee ? (
                  <li
                    aria-hidden="true"
                    className={styles["announcement-bar__separator"]}
                    data-testid="announcement-separator"
                  >
                    <svg
                      className={styles["announcement-bar__separator-icon"]}
                      viewBox="0 0 24 24"
                      width="12"
                      height="12"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 12h.01"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </li>
                ) : null}
              </Fragment>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default AnnouncementBar
